import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../services/api";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = {
  import: "/performance/history/overall-ratings/import",
};

const normalizeKey = (k) =>
  String(k || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

const pickFirst = (obj, keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "") return obj[k];
  }
  return undefined;
};

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function ImportOverallRatingsHistoryPage() {
  const [file, setFile] = useState(null);
  const [period, setPeriod] = useState("");
  const [source, setSource] = useState("");
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [rows, setRows] = useState([]); // parsed rows (normalized)
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const preview = useMemo(() => rows.slice(0, 10), [rows]);

  const resetAll = () => {
    setFile(null);
    setRows([]);
    setResult(null);
    setError("");
  };

  const parseFile = async (f) => {
    setParsing(true);
    setError("");
    setResult(null);

    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });

      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("No sheet found in file.");

      const ws = wb.Sheets[sheetName];

      // Read as rows (arrays) first
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

      if (!raw || raw.length === 0) throw new Error("File is empty.");

      const firstRow = raw[0].map((x) => String(x).trim());
      const looksLikeHeader = firstRow.some((c) => {
        const k = normalizeKey(c);
        return ["employee_code", "employee_id", "emp_code", "code", "overall_rating", "rating", "score", "overall"].includes(k);
      });

      let normalizedRows = [];

      if (looksLikeHeader) {
        // Use header row
        const header = raw[0].map((h) => normalizeKey(h));
        const dataRows = raw.slice(1);

        normalizedRows = dataRows
          .filter((r) => r.some((cell) => String(cell).trim() !== ""))
          .map((r) => {
            const obj = {};
            header.forEach((h, idx) => (obj[h] = r[idx]));
            return obj;
          });
      } else {
        // No header: assume col0 employee_code, col1 overall_rating
        const dataRows = raw.filter((r, idx) => idx !== 0 || r.some((cell) => String(cell).trim() !== ""));
        normalizedRows = dataRows
          .filter((r) => r.some((cell) => String(cell).trim() !== ""))
          .map((r) => ({
            employee_code: r[0],
            overall_rating: r[1],
          }));
      }

      // Cleanup + map to what backend expects: items.*.employee_id + overall_rating
      const cleaned = [];
      const seen = new Set();
      const localErrors = [];

      normalizedRows.forEach((r, i) => {
        const obj = {};
        // Normalize keys if came from header mode
        Object.keys(r || {}).forEach((k) => (obj[normalizeKey(k)] = r[k]));

        const emp =
          pickFirst(obj, ["employee_code", "employee_id", "emp_code", "code"]) ??
          ""; // could still be number

        const rating =
          pickFirst(obj, ["overall_rating", "rating", "score", "overall"]) ?? "";

        const employee_id = String(emp).trim();
        const overall_rating = String(rating).trim();

        if (!employee_id) {
          localErrors.push({ row: i + 1, employee_id: "", message: "Missing employee_code" });
          return;
        }

        // de-dupe in frontend (same rule as backend: user_id + period, but we only have employee_code here)
        // so we de-dupe by employee_code (last wins) -> easiest: overwrite
        // using map behavior:
        const key = employee_id;
        if (seen.has(key)) {
          // allow overwrite: remove previous then re-add (we’ll handle by pushing and later compress)
        }
        seen.add(key);

        if (overall_rating !== "" && Number.isNaN(Number(overall_rating))) {
          localErrors.push({ row: i + 1, employee_id, message: "overall_rating is not numeric" });
          return;
        }

        cleaned.push({
          employee_id, // ✅ backend expects employee_id (you handle employee_code inside controller)
          overall_rating: overall_rating === "" ? null : Number(overall_rating),
          __row: i + 1,
        });
      });

      // compress duplicates by keeping last occurrence
      const lastByEmp = new Map();
      cleaned.forEach((it) => lastByEmp.set(it.employee_id, it));
      const finalRows = Array.from(lastByEmp.values());

      setRows(finalRows);
      if (localErrors.length) {
        setError(`Parsed with ${localErrors.length} row issue(s). You can still import valid rows.`);
      }
    } catch (e) {
      setRows([]);
      setError(e?.message || "Failed to parse file.");
    } finally {
      setParsing(false);
    }
  };

  const runImport = async () => {
    setError("");
    setResult(null);

    const p = String(period || "").trim();
    if (!p) {
      setError("Period is required (ex: 2024).");
      return;
    }
    if (!rows.length) {
      setError("No valid rows to import.");
      return;
    }

    setImporting(true);

    try {
      // Avoid large payload errors: send batches
      const CHUNK_SIZE = 800; // adjust if needed
      const chunks = chunkArray(rows, CHUNK_SIZE);

      let insertedOrUpdated = 0;
      let skippedCount = 0;
      const skipped = [];

      for (let i = 0; i < chunks.length; i++) {
        const payload = {
          period: p,
          source: source?.trim() || null,
          items: chunks[i].map((r) => ({
            employee_id: r.employee_id,
            overall_rating: r.overall_rating,
          })),
        };

        const res = await api.post(API.import, payload);
        const data = res?.data || {};

        insertedOrUpdated += Number(data.inserted_or_updated || 0);
        skippedCount += Number(data.skipped_count || 0);
        if (Array.isArray(data.skipped)) skipped.push(...data.skipped);
      }

      setResult({
        message: "Import completed",
        period: p,
        inserted_or_updated: insertedOrUpdated,
        skipped_count: skippedCount,
        skipped,
      });
    } catch (e) {
      setError(e?.response?.data?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid rgba(0,0,0,0.12)" }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Import Overall Ratings History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload Excel/CSV → we parse it in frontend → send JSON items to save in history table.
            </Typography>
          </Box>

          <Divider />

          {parsing && <LinearProgress />}
          {importing && <LinearProgress />}

          {error && (
            <Alert severity="warning" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {result && (
            <Alert severity="success" onClose={() => setResult(null)}>
              Imported/Updated: {result.inserted_or_updated} — Skipped: {result.skipped_count} — Period: {result.period}
            </Alert>
          )}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={parsing || importing}
              sx={{ whiteSpace: "nowrap" }}
            >
              Choose Excel/CSV
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setFile(f);
                  parseFile(f);
                }}
              />
            </Button>

            <Typography variant="body2" sx={{ flex: 1 }}>
              {file ? file.name : "No file selected"}
            </Typography>

            <TextField
              size="small"
              label="Period *"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2024"
              disabled={parsing || importing}
              sx={{ minWidth: 180 }}
            />

            <TextField
              size="small"
              label="Source (optional)"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="HR Excel"
              disabled={parsing || importing}
              sx={{ minWidth: 220 }}
            />

            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={runImport}
              disabled={parsing || importing || !rows.length || !period.trim()}
              sx={{ whiteSpace: "nowrap" }}
            >
              Import
            </Button>

            <Button
              variant="text"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={resetAll}
              disabled={parsing || importing}
              sx={{ whiteSpace: "nowrap" }}
            >
              Reset
            </Button>
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">
              Parsed Rows: <b>{rows.length}</b>
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip label="employee_code/employee_id" size="small" variant="outlined" />
              <Chip label="overall_rating/score/rating" size="small" variant="outlined" />
            </Stack>
          </Stack>

          {/* Preview */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Employee Code</TableCell>
                  <TableCell width={120}>Overall Rating</TableCell>
                  <TableCell width={120}>Source Row</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ color: "text.secondary" }}>
                      No rows parsed yet. Upload a file.
                    </TableCell>
                  </TableRow>
                ) : (
                  preview.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{r.employee_id}</TableCell>
                      <TableCell>{r.overall_rating ?? "—"}</TableCell>
                      <TableCell>{r.__row}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Skipped list */}
          {!!(result?.skipped?.length) && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Skipped (user not found)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                These employee codes were not found in users table.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={200}>Employee</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.skipped.slice(0, 50).map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{s.employee_id}</TableCell>
                      <TableCell>{s.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {result.skipped.length > 50 && (
                <Typography variant="caption" color="text.secondary">
                  Showing first 50 only.
                </Typography>
              )}
            </Paper>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
